import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Handshake, ArrowRight, LogIn, User, Briefcase, Users, ShieldCheck } from 'lucide-react';

/**
 * Portal hub — fits the visible viewport (below the marketing header) on any
 * 720p+ laptop. The marketing Footer renders BELOW the fold (scroll to reach),
 * but everything a visitor needs to decide where to go is visible immediately.
 *
 * Mental model = 2 tiers:
 *   1. New here?       → 2 sign-up cards.
 *   2. Coming back?    → 4 compact sign-in buttons + Forgot-password.
 */

const SIGN_UPS = [
  {
    title: 'Apply as a student',
    description: '~5 min. Submit your details + documents and we\'ll match you to a college program.',
    cta: 'Start application',
    path: '/student-signup',
    Icon: GraduationCap,
    accent: 'gold',
    testid: 'signup-card-student',
  },
  {
    title: 'Join as a recruiter',
    description: 'Become a recruitment partner. Submit your business licence and ID for approval.',
    cta: 'Become a partner',
    path: '/recruiter-signup',
    Icon: Handshake,
    accent: 'navy',
    testid: 'signup-card-recruiter',
  },
];

const SIGN_INS = [
  { label: 'Student', path: '/student-login', Icon: User, testid: 'signin-student' },
  { label: 'Recruiter', path: '/recruiter-login', Icon: Briefcase, testid: 'signin-recruiter' },
  { label: 'Staff', path: '/staff-login', Icon: Users, testid: 'signin-staff' },
  { label: 'Admin', path: '/admin-login', Icon: ShieldCheck, testid: 'signin-admin' },
];

// Marketing header is ~80–88 px tall. Subtract that from the viewport so our
// hub fills the remaining visible area exactly — without pushing the footer up.
const HEIGHT = 'min-h-[calc(100dvh-88px)]';

export default function LoginPage() {
  const navigate = useNavigate();

  return (
    <div className={`${HEIGHT} flex items-center justify-center bg-tge-paper px-4 py-4`}>
      <div className="w-full max-w-5xl">
        {/* Hero (compact) */}
        <div className="text-center mb-4" data-testid="login-hub-hero">
          <p className="uppercase tracking-[0.18em] text-[10px] font-semibold text-tge-gold mb-1">Welcome</p>
          <h1 className="font-serif-display text-2xl sm:text-3xl lg:text-[34px] text-tge-navy leading-tight">
            Are you new here, or coming back?
          </h1>
        </div>

        {/* Tier 1: sign-ups */}
        <section className="mb-4" data-testid="login-hub-signups">
          <div className="flex items-center gap-3 mb-2 px-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-tge-navy">New here</span>
            <span className="flex-1 h-px bg-tge-navy/10" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {SIGN_UPS.map((c) => {
              const isGold = c.accent === 'gold';
              return (
                <article
                  key={c.title}
                  data-testid={c.testid}
                  onClick={() => navigate(c.path)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(c.path)}
                  className={`group relative cursor-pointer rounded-2xl p-4 sm:p-5 overflow-hidden transition-all duration-300 border-2 ${
                    isGold
                      ? 'bg-white border-tge-gold/30 hover:border-tge-gold hover:shadow-[0_12px_36px_rgba(212,175,55,0.18)]'
                      : 'bg-tge-navy border-tge-navy text-white hover:shadow-[0_12px_36px_rgba(27,42,107,0.30)]'
                  } hover:-translate-y-0.5`}
                >
                  <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full blur-3xl opacity-50 pointer-events-none ${isGold ? 'bg-tge-gold/20' : 'bg-tge-gold/15'}`} />
                  <div className="flex items-start gap-3.5">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-md ${isGold ? 'bg-tge-gold text-white' : 'bg-tge-gold text-tge-navy-deep'}`}>
                      <c.Icon size={18} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className={`font-serif-display text-lg sm:text-xl leading-tight mb-0.5 ${isGold ? 'text-tge-navy' : 'text-white'}`}>
                        {c.title}
                      </h2>
                      <p className={`text-[12px] leading-snug ${isGold ? 'text-tge-muted' : 'text-tge-cream/85'}`}>
                        {c.description}
                      </p>
                      <span
                        className="mt-2 inline-flex items-center gap-1.5 font-semibold text-[12px] text-tge-gold group-hover:gap-2.5 transition-all"
                        data-testid={`${c.testid}-cta`}
                      >
                        {c.cta}
                        <ArrowRight size={13} />
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Tier 2: sign-ins */}
        <section data-testid="login-hub-signins">
          <div className="flex items-center gap-3 mb-2 px-1">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-tge-navy">Already have an account?</span>
            <span className="flex-1 h-px bg-tge-navy/10" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {SIGN_INS.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => navigate(s.path)}
                data-testid={s.testid}
                className="group flex items-center gap-2.5 bg-white border border-tge-navy/10 hover:border-tge-gold rounded-xl px-3 py-2.5 transition-all hover:shadow-[0_4px_16px_rgba(27,42,107,0.08)] text-left"
              >
                <span className="w-8 h-8 rounded-lg bg-tge-cream-soft group-hover:bg-tge-gold/20 flex items-center justify-center transition-colors shrink-0">
                  <s.Icon size={14} className="text-tge-navy" strokeWidth={1.8} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[9px] uppercase tracking-[0.14em] text-tge-muted font-semibold leading-tight">Sign in as</span>
                  <span className="block text-[13px] font-semibold text-tge-navy truncate leading-tight">{s.label}</span>
                </span>
                <LogIn size={12} className="text-tge-navy/40 group-hover:text-tge-gold transition-colors shrink-0" />
              </button>
            ))}
          </div>

          <p className="text-center text-[11px] text-tge-muted mt-2.5">
            Forgot your password?{' '}
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-tge-navy font-semibold underline decoration-tge-gold/50 underline-offset-4 hover:decoration-tge-gold"
              data-testid="login-hub-forgot"
            >
              Reset it here
            </button>
          </p>
        </section>
      </div>
    </div>
  );
}
